// import Dexie from 'dexie';
import {resSuccess as success, resError as error} from '@Mocks/data/resObj.js';
import bundleTags from '@Mocks/payload/getBundleTags.js';
import {
	bundlesMock,
	bundleTagsMock,
	accessoriesData,
	brandsData,
	seriesData,
	productsData,
} from '@Mocks/data/bundles.mock.js';
import BaseDB from './db-base.js';
//
class MockRepo {
	constructor($log, $q, ENV) {
		'ngInject';
		this.$q = $q;
		this.ENV = ENV;
		this.base = new BaseDB('mock-db', $log);
		this._seedAll();
	}

	_seedAll() {
		// seed models
		const bundles = bundlesMock.map((b, i) => ({
			...b,
			buid: this.base.uuid(),
			_collapsed: true,
			products_list: b.products_list.map((p, j) => ({
				...p,
				product_id: 1391689352341 + i * 4 + j, // unique across all items
			})),
		}));
		this.base.db.bundles.bulkPut(bundles).catch(err => this.$log.error(err));

		// bundles price, builder
		const prices = bundles.map((b, i) => ({
			universal_unique_id: b.buid,
			bundle_equipment_tax_paid_value: 0,
			bundle_adjustment_buffer_value: 0,
			bundle_product_price: this.base.mockPrice(b.buid),
			products_list: b.products_list.map(p => ({
				product_number: p.product_number,
				category_id: p.category_id,
				business_unit_id: p.business_unit_id,
				product_cost: p.retail_cost,
				quantity: p.category_id % 10,
				product_availability: (p.category_id % 10) * i,
				retail_cost: this.base.mockPrice(p.product_number),
			})),
		}));
		this.base.db.prices.bulkPut(prices).catch(err => this.$log.error(err));

		// bundles price, bundle manager
		const brices = bundles.map(b => ({
			universal_unique_id: b.buid,
			bundle_product_price: this.base.mockPrice(b.buid),
			calculated_gross_margin: parseFloat((this.base.mockPrice(b.buid) * 15).toFixed(2)),
			gross_profit: parseFloat((this.base.mockPrice(b.buid) / 15).toFixed(2)),
		}));
		this.base.db.brices.bulkPut(brices).catch(err => this.$log.error(err));

		// suggested tags
		this.base.db.tags.bulkPut(bundleTagsMock).catch(err => this.$log.error(err));

		// accessories
		this.base.db.accessories.bulkPut(accessoriesData).catch(err => this.$log.error(err));

		// brands
		this.base.db.brands.bulkPut(brandsData).catch(err => this.$log.error(err));

		// series
		this.base.db.series.bulkPut(seriesData).catch(err => this.$log.error(err));

		// products
		this.base.db.products.bulkPut(productsData).catch(err => this.$log.error(err));
	}

	async getAllBundlesForProposal(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.bundles.toArray();
		return {data: {payLoad: {proposal_options_for_customer: data}, success}};
	}

	async getListBySystemTypeAndCapacity(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.bundles.toArray();
		return {data: {payLoad: {proposal_options_for_customer: data}, success}};
	}

	async getActivationStatus(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		return this.$q.when({data: {payLoad: [], success}});
	}

	async getBundlePriceAndAvailabilityForSystemPreferences(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.prices.toArray();
		return {data: {payLoad: data, success}};
	}

	async getRecommendedTags(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.tags.toArray();
		return {data: {payLoad: data, success}};
	}

	async getListBySystemType(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.bundles.toArray();
		return this.transformBundles(data);
	}

	async getAllBundlesForPricingBook(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const data = await this.base.db.bundles.toArray();
		return this.transformBundles(data);
	}

	transformBundles(data) {
		const storedBundles3Tons = data.map((bundle, idx) => ({
			position: idx + 1,
			bundle_product_id: bundle.bundle_product_id,
			retail_price: bundle.retail_price,
			gross_profit_target: bundle.gross_profit_target,
			business_unit_id: bundle.business_unit_id,
			ahri_data: bundle.ahri_data,
			tags: bundle.tags,
			buid: bundle.buid,
			products_list: bundle.products_list.map(p => ({
				brand_id: p.brand_id,
				business_unit_id: p.business_unit_id,
				capacity: p.capacity,
				category_id: p.category_id,
				category_name: p.category_name,
				category_type: p.category_type,
				component_type_id: p.component_type_id,
				custom_component_btu: p?.custom_component_btu ?? null,
				custom_component_tonnage_decimal: p?.custom_component_tonnage_decimal ?? p.capacity,
				lookup_component_type_name: p?.lookup_component_type_name ?? null,
				model_number: p.product_number,
				product_cost: p?.product_cost || p.retail_cost,
				product_id: p.product_id,
				template_area: 'P',
				unit_type: p.unit_type,
			})),
		}));
		const bundleData = [1.5, 2, 2.5, 3, 3.5, 4, 5].map(t => ({
			capacity: t,
			guid: this.base.uuid(),
			systems: t !== 3 ? [] : storedBundles3Tons,
		}));
		return {data: {payLoad: structuredClone(bundleData), success}};
	}

	async getBundlePriceForPricingBook(params) {
		const bundlesId = JSON.parse(params.bundlesInJsonFormat).map(b => b.universal_unique_id);
		const data = await this.base.db.brices.bulkGet(bundlesId);
		return {data: {payLoad: data, success}};
	}

	async postUpdateBundle(params) {
		if (this.isDEV()) this.base.$log.info('log', params);
		const _stored = await this.base.db.bundles.get(params.bundleId);
		_stored.products_list = params.updatedEquipmentJsonData;
		await this.base.db.bundles.update(params.bundleId, _stored); // return number of updated records

		const _bundle = await this.base.db.bundles.get(params.bundleId);
		return {data: {payLoad: _bundle, success}};
	}

	async getAccessories(params) {
		if (this.isDEV()) this.base.$log.info('getAccessories', params);
		const data = await this.base.db.accessories.toArray();
		return {data: {payLoad: {accessories: data, total_of_records: data.length}, success}};
	}

	async getAllProviderBrandsWithSeriesSelection(params) {
		if (this.isDEV()) this.base.$log.info('getAllProviderBrandsWithSeriesSelection', params);
		const data = await this.base.db.brands.toArray();
		return {data: {payLoad: {brands: data, total_of_records: data.length}, success}};
	}
	async getAllProviderBrandsWithCustomSeriesSelection(params) {
		if (this.isDEV()) this.base.$log.info('getAllProviderBrandsWithCustomSeriesSelection', params);
		const data = await this.base.db.brands.toArray();
		return {data: {payLoad: data, success}};
	}

	async getAlternateSeries(params) {
		if (this.isDEV()) this.base.$log.info('getAlternateSeries', params);
		const seriesIds =
			params.category === 'Condenser'
				? [660, 661, 662, 663]
				: params.category === 'Furnace'
					? [664, 665, 666, 667]
					: [668, 669, 670, 671];
		const data = await this.base.db.series.bulkGet(seriesIds);
		return {data: {payLoad: data, success}};
	}
	async getCustomAlternateSeries(params) {
		if (this.isDEV()) this.base.$log.info('getCustomAlternateSeries', params);
		const data = await this.base.db.series.toArray();
		return {data: {payLoad: data, success}};
	}

	async getAlternateEquipment(params) {
		if (this.isDEV()) this.base.$log.info('getAlternateEquipment', params);
		const {seriesId} = params;
		const data = await this.base.db.products.toArray();
		const allTags = await this.base.db.tags.toArray();
		const alt = data.filter(s => s.series_id === seriesId);
		const tags =
			params.category === 'Condenser'
				? allTags.filter(
						t => t.key === 'tier:premium' || t.key === 'tax:eligible' || t.key === 'promo:summer'
					)
				: params.category === 'Furnace'
					? allTags.filter(t => t.key === 'tier:premium' || t.key === 'tax:2025')
					: allTags.filter(t => t.key === 'tier:premium');
		const alternates = alt.map((p, i) => {
			if (i === 0) p.tags = tags;
			return p;
		});
		return {data: {payLoad: alternates, success}};
	}

	async replaceEquipmentInBundle(params) {
		if (this.isDEV()) this.base.$log.info('replaceEquipmentInBundle', params);
		// get changed bundle
		// const bundles = await this.base.db.bundles.toArray();
		// const bundle = bundles.find(p => p.bundle_product_id === params.bundleProductId);
		// const changes = this.findDifference(
		// 	bundle.products_list.map(p => p.product_id),
		// 	params.equipmentsJson.map(p => p.product_id)
		// );
		// const updatedProductList = bundle.products_list.map(p => {
		// 	if (changes.includes(change[0])) p.product_is_selected = false;
		// 	return p;
		// });
		// const updated = await this.base.db.bundles.update(params.bundleProductId, bundle);
		const updated = true;
		const data = {bundle_product_name: `OnCall Air OCA1436 AC 3 Ton`, has_replacement: true};
		if (updated) return {data: {payLoad: data, success}};
		return {data: {error}};
	}

	findDifference(arr1, arr2) {
		return arr1.filter(item => !arr2.includes(item));
	}

	async getCustomEquipmentsBySeriesAndCapacity(params) {
		if (this.isDEV()) this.base.$log.info('getCustomEquipmentsBySeriesAndCapacity', params);
		const data = await this.base.db.products.toArray();
		return {data: {payLoad: data, success}};
	}

	async getAlternateEquipmentPricesAndAvailability(params) {
		if (this.isDEV()) this.base.$log.info('getAlternateEquipmentPricesAndAvailability', params);
		const data = await this.base.db.products.toArray();
		return {
			data: {
				payLoad: data.map(p => ({
					availability: 3,
					cost: this.base.mockPrice(p.product_number),
					id: p.product_id,
					is_available: true,
					updated: p.price_last_update || '08-25-2025 02:47:48',
					tags: p.tags,
				})),
				success,
			},
		};
	}

	async postAddSystem(params) {
		if (this.isDEV()) this.base.$log.info('addSystemToProposal', params);
		return {
			data: {
				payLoad: {consultation_id: 1729526, label: 'Fantastic', proposal_id: 4570999, system_id: 5067202},
				success,
			},
		};
	}

	async getNewSystemConfiguration(params) {
		if (this.isDEV()) this.base.$log.info('addSystemToProposal', params);
		const _data = {
			system_type_id: 4,
			system_type_display_name: 'Split AC With Furnace and Coil',
			system_type_name: 'straight_cool_plus_furnace',
			capacity: '3.00',
			equipment_pricing_profile_id: 345,
			equipment_pricing_profile_name: 'Default',
		};
		return {data: {payLoad: _data, success}};
	}

	async postPricingdetails(params) {
		if (this.isDEV()) this.base.$log.info('postPricingdetails', params);
		return {data: {payLoad: {}, success}};
	}

	async getTags(params) {
		if (this.isDEV()) this.base.$log.info('getTags', params);
		return {data: {payLoad: bundleTags, success}};
	}

	isDEV() {
		// return false;
		return this.ENV === 'development' || this.ENV === 'local';
	}
}
//
let svcMod = angular.module('mod.svc.APIDbSvc', []).service('APIDbSvc', MockRepo);
export default (svcMod = svcMod.name);
